import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { TopBar } from "../components/Navigation";
import MapView from "../components/MapView";
import AdventureCard from "../components/AdventureCard";
import {
    MapPin, Crown, Lock, Star, Plus, X, GripVertical,
    Mountain, Calendar, Cloud, Signal, Users, Dog, Baby,
    ShieldCheck, Compass, Camera, BookOpen, Backpack, Heart, ImageIcon,
    Sparkles, Save, Eye, Send, Film, Map as MapIcon, ShoppingBag,
} from "lucide-react";
import { toast } from "sonner";
import { MultiImageUploader, resolveImageUrl } from "../components/ImageUploader";
import MultiVideoUploader from "../components/VideoUploader";
import BeforeAfterUploader from "../components/BeforeAfterUploader";
import MarketplaceGearPicker from "../components/MarketplaceGearPicker";
import ActivityPicker from "../components/ActivityPicker";

// ---------- constants ----------
const DIFFICULTIES = ["easy", "moderate", "hard", "expert"];
const PHYSICAL = ["low", "medium", "high"];
const TECHNICAL = ["beginner", "intermediate", "advanced"];
const ROUTE_TYPES = [
    { id: "loop", label: "Loop" },
    { id: "out_and_back", label: "Out-and-back" },
    { id: "point_to_point", label: "Point-to-point" },
];
const SEASONS = ["spring", "summer", "fall", "winter"];
const GROUP_TYPES = ["solo", "family", "friends", "guided"];
const LAYOUT_STYLES = [
    { id: "story", label: "Story", desc: "Narrative trip report" },
    { id: "guide", label: "Guide", desc: "Step-by-step beta" },
    { id: "route", label: "Route", desc: "Map + stats forward" },
    { id: "gallery", label: "Gallery", desc: "Photo-led" },
    { id: "review", label: "Review", desc: "Ratings + verdict" },
];
const TERRAIN = ["dirt", "rock", "sand", "snow", "water", "forest", "alpine", "desert"];
const WEATHER_OPTS = ["sunny", "rainy", "windy", "snow", "extreme heat", "cold"];
const WATER_CROSSINGS = ["none", "minor", "major"];
const CELL_SERVICE = ["good", "spotty", "none"];
const CROWDS = ["empty", "light", "moderate", "packed"];
const VEHICLES = ["2WD", "AWD", "4x4", "motorcycle", "boat", "none"];
const BEST_FOR_OPTS = [
    "beginners", "families", "experts", "hunters", "anglers",
    "overlanders", "kids", "dogs", "photographers", "solo travelers",
];
const TRAIL_CONDITIONS = ["clear", "muddy", "snowy", "washed out", "overgrown", "icy"];
const WORTH_IT = [
    { id: "worth_it", label: "Worth the drive", emoji: "🏔" },
    { id: "maybe", label: "Maybe", emoji: "🤔" },
    { id: "lesson_learned", label: "Lesson learned", emoji: "🪦" },
];
const RECOMMEND = [
    { id: "yes", label: "Absolutely" },
    { id: "maybe", label: "Maybe" },
    { id: "no", label: "Not really" },
];
const RATING_KEYS = [
    { key: "overall", label: "Overall experience" },
    { key: "scenic", label: "Scenic value" },
    { key: "difficulty_accuracy", label: "Difficulty accuracy" },
    { key: "crowd_level", label: "Crowd level" },
    { key: "family_friendly", label: "Family friendliness" },
];

const STORY_PROMPTS = [
    { key: "story_worth_sharing", label: "What made this adventure worth sharing?", placeholder: "The ridgeline at sunrise alone was worth every step…" },
    { key: "story_before_you_go", label: "What should someone know before going?", placeholder: "Permits sell out by April, plan ahead…" },
    { key: "story_best_part", label: "Best part of the trip?", placeholder: "" },
    { key: "story_do_differently", label: "What would you do differently?", placeholder: "" },
    { key: "story_safety_concerns", label: "Safety concerns to flag", placeholder: "" },
];

const PAID_TIERS = new Set(["adventure", "premium"]);
const isPaid = (user) => !!(user && PAID_TIERS.has(user.tier));

// Optional detail sections — each is opt-in via the "Add more details" chooser.
const MORE_SECTIONS = [
    { key: "trip", label: "Trip details", icon: Calendar },
    { key: "route", label: "Route & distance", icon: MapPin },
    { key: "conditions", label: "Conditions", icon: Cloud },
    { key: "media", label: "Video & before/after", icon: Film },
    { key: "story", label: "Full story", icon: BookOpen },
    { key: "gear", label: "Gear used", icon: Backpack },
    { key: "ratings", label: "Ratings & verdict", icon: Star },
    { key: "safety", label: "Safety notes", icon: ShieldCheck },
    { key: "tags", label: "Tags", icon: Eye },
];

// ---------- initial form ----------
const blankForm = () => ({
    // Basics
    title: "", summary: "", description: "", activity: "hiking",
    date: "", date_end: "", season: "", best_time_to_go: "",
    group_type: "", layout_style: "story",

    // Location & route
    location_name: "", general_area: "", lat: "", lng: "",
    start_point: "", end_point: "", route_type: "",
    distance_km: "", elevation_gain_m: "", duration_hours: "",
    terrain: [], hide_exact_location: false, sensitive_location: false,
    route: [], gpx_url: "",

    // Difficulty & conditions
    difficulty: "moderate", physical_demand: "", technical_skill: "",
    trail_condition: "", weather: "", water_crossings: "",
    cell_service: "", crowds: "",
    permit_required: false, fees_required: false,
    kid_friendly: false, dog_friendly: false,

    // Photos
    photos: [], cover_photo: "", photo_captions: {},
    videos: [], before_after: [], map_screenshots: [],

    // Story prompts (also concatenated into description on submit)
    story_worth_sharing: "", story_before_you_go: "",
    story_best_part: "", story_do_differently: "", story_safety_concerns: "",

    // Gear
    gear_used: [], must_haves: [], nice_to_haves: [],
    wished_i_brought: "", safety_gear: [], vehicle_needed: "",
    linked_gear: [],

    // Recommendations
    best_for: [], would_recommend: "", worth_it_badge: "",
    tips_for_others: "", ratings: {},

    // Safety & ethics
    leave_no_trace: false,
    wildlife_notes: "", fire_restrictions: "",
    trail_closure_warning: "", private_land_warning: false,
    required_licenses: "",

    // Tags + privacy
    tags: "",
    visibility: "public",
    allow_comments: true, allow_saves: true,
    status: "completed",
});

// ---------- main page ----------
export default function CreateAdventure() {
    const navigate = useNavigate();
    const { id: editId } = useParams();
    const isEditMode = !!editId;
    const [searchParams] = useSearchParams();
    const focusSection = searchParams.get("focus"); // route | gear | safety (from "turn into guide" nudge)
    const { user } = useAuth();
    const paid = isPaid(user);

    const [form, setForm] = useState(blankForm);
    const [loading, setLoading] = useState(false);
    const [savingDraft, setSavingDraft] = useState(false);
    const [draftLoaded, setDraftLoaded] = useState(false);
    const [open, setOpen] = useState(() =>
        isEditMode ? Object.fromEntries(MORE_SECTIONS.map((s) => [s.key, true])) : {}
    );
    const toggleSection = (k) => setOpen((o) => ({ ...o, [k]: !o[k] }));
    const [lastSavedAt, setLastSavedAt] = useState(null);
    const draftHash = useRef("");

    // ----- patch helper -----
    const patch = useCallback((updates) => {
        setForm((f) => ({ ...f, ...updates }));
    }, []);

    // ----- edit mode: load existing adventure -----
    useEffect(() => {
        if (!isEditMode) return;
        let cancel = false;
        (async () => {
            try {
                const { data } = await api.get(`/adventures/${editId}`);
                if (cancel) return;
                if (data.author?.user_id && user && data.author.user_id !== user.user_id) {
                    toast.error("You can only edit your own adventures.");
                    navigate(`/adventures/${editId}`);
                    return;
                }
                // Build a safe form patch from the API doc:
                //   - numeric fields → strings (form inputs need strings)
                //   - array fields → always arrays (never null)
                //   - object fields → always objects (never null)
                //   - tags → comma-string (form uses a single text input)
                const num2str = (v) => (v === null || v === undefined ? "" : String(v));
                const arr = (v) => (Array.isArray(v) ? v : []);
                const obj = (v) => (v && typeof v === "object" && !Array.isArray(v) ? v : {});
                const str = (v) => (v === null || v === undefined ? "" : v);
                const blank = blankForm();
                const next = { ...blank };
                // Copy every known form key from data, with type-safe coercion.
                for (const k of Object.keys(blank)) {
                    if (data[k] === undefined) continue;
                    const dv = data[k];
                    if (Array.isArray(blank[k])) next[k] = arr(dv);
                    else if (blank[k] && typeof blank[k] === "object") next[k] = obj(dv);
                    else if (typeof blank[k] === "boolean") next[k] = !!dv;
                    else next[k] = str(dv);
                }
                // Numeric form inputs need strings.
                next.lat = num2str(data.lat);
                next.lng = num2str(data.lng);
                next.distance_km = num2str(data.distance_km);
                next.elevation_gain_m = num2str(data.elevation_gain_m);
                next.duration_hours = num2str(data.duration_hours);
                // Tags: API returns array, form uses comma-separated string.
                next.tags = Array.isArray(data.tags) ? data.tags.join(", ") : str(data.tags);
                // Always unstitch the description so the freeform field is
                // truly freeform. `buildDescription` re-stitches story_* into
                // it on every save — if we don't strip here, every save adds
                // another duplicated copy of the story prompts to description.
                // Unstitcher is safe: it only matches blocks that START with
                // `**<known prompt label>**\n` so inline `**bold**` is preserved.
                if (next.description && /\*\*.+?\*\*\n/.test(next.description)) {
                    const parsed = unstitchDescription(next.description);
                    // Prefer the freshly-parsed story_* fields when they were
                    // extracted from the description; otherwise keep whatever
                    // the API returned in next.story_* (post-persistence-fix).
                    next.description = parsed.description ?? next.description;
                    STORY_PROMPTS.forEach((p) => {
                        if (parsed[p.key]) next[p.key] = parsed[p.key];
                    });
                    if (parsed.tips_for_others) next.tips_for_others = parsed.tips_for_others;
                    if (parsed.wished_i_brought) next.wished_i_brought = parsed.wished_i_brought;
                }
                setForm(next);
                setDraftLoaded(true); // skip draft autoload behavior in edit mode
            } catch (e) {
                toast.error(formatApiError(e));
                navigate("/feed");
            }
        })();
        return () => { cancel = true; };
    }, [isEditMode, editId, user, navigate]);

    // ----- "Turn this log into a guide" deep-link: open + scroll to a section -----
    useEffect(() => {
        if (!isEditMode || !draftLoaded || !focusSection) return;
        const testidMap = { route: "section-location", gear: "section-gear", safety: "section-safety" };
        const testid = testidMap[focusSection];
        if (!testid) return;
        setOpen((o) => ({ ...o, [focusSection]: true }));
        const t = setTimeout(() => {
            const el = document.querySelector(`[data-testid="${testid}"]`);
            if (el) {
                el.scrollIntoView({ behavior: "smooth", block: "center" });
                el.classList.add("ring-2", "ring-ember-orange", "rounded-xl");
                setTimeout(() => el.classList.remove("ring-2", "ring-ember-orange", "rounded-xl"), 2400);
            }
        }, 500);
        return () => clearTimeout(t);
    }, [isEditMode, draftLoaded, focusSection]);

    // ----- load existing draft on mount (paid only, NEW adventure flow only) -----
    useEffect(() => {
        if (isEditMode) return;            // editing an existing post — don't load draft
        if (!paid) { setDraftLoaded(true); return; }
        let cancel = false;
        (async () => {
            try {
                const { data } = await api.get("/drafts");
                if (!cancel && data.draft?.data) {
                    setForm((f) => ({ ...f, ...data.draft.data }));
                    setLastSavedAt(data.draft.updated_at);
                    toast.success("Draft restored");
                }
            } finally {
                if (!cancel) setDraftLoaded(true);
            }
        })();
        return () => { cancel = true; };
    }, [paid, isEditMode]);

    // ----- autosave draft (paid only, NEW adventure flow only) -----
    useEffect(() => {
        if (isEditMode || !paid || !draftLoaded) return;
        const snapshot = JSON.stringify(form);
        if (snapshot === draftHash.current) return;
        const t = setTimeout(async () => {
            try {
                draftHash.current = snapshot;
                const { data } = await api.put("/drafts", { data: form });
                setLastSavedAt(data.updated_at);
            } catch {
                /* swallow — autosave is best-effort */
            }
        }, 1500);
        return () => clearTimeout(t);
    }, [form, paid, draftLoaded]);

    // ----- manual save -----
    const saveDraft = async () => {
        if (!paid) return;
        setSavingDraft(true);
        try {
            const { data } = await api.put("/drafts", { data: form });
            setLastSavedAt(data.updated_at);
            toast.success("Draft saved");
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSavingDraft(false);
        }
    };

    // ----- publish / save changes -----
    const handleSubmit = async (e) => {
        e?.preventDefault();
        if (!form.title.trim()) {
            toast.error(isEditMode ? "Title can't be empty" : "Add a title before publishing");
            return;
        }
        setLoading(true);
        try {
            const description = buildDescription(form);
            const payload = {
                ...form,
                description,
                lat: form.lat ? parseFloat(form.lat) : null,
                lng: form.lng ? parseFloat(form.lng) : null,
                distance_km: form.distance_km ? parseFloat(form.distance_km) : null,
                elevation_gain_m: form.elevation_gain_m ? parseFloat(form.elevation_gain_m) : null,
                duration_hours: form.duration_hours ? parseFloat(form.duration_hours) : null,
                tags: typeof form.tags === "string"
                    ? form.tags.split(",").map((t) => t.trim()).filter(Boolean)
                    : (form.tags || []),
                route: form.route || [],
            };
            if (isEditMode) {
                await api.patch(`/adventures/${editId}`, payload);
                toast.success("Adventure updated");
                navigate(`/adventures/${editId}`);
            } else {
                const { data } = await api.post("/adventures", payload);
                // Wipe draft on successful publish
                if (paid) {
                    api.delete("/drafts").catch(() => {});
                }
                toast.success("Adventure published");
                navigate(`/adventures/${data.id}`);
            }
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    const completion = useMemo(() => computeCompletion(form), [form]);
    const previewAdventure = useMemo(() => buildPreview(form, user), [form, user]);

    return (
        <div data-testid="create-adventure-page">
            <TopBar title={isEditMode ? "Edit Adventure" : "Create Adventure"} />

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-10">
                {/* Hero */}
                <div className="mb-8">
                    <div className="text-xs font-bold uppercase tracking-[0.25em] text-ember-orange mb-2">
                        Adventure Log
                    </div>
                    <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl text-midnight-pine tracking-tighter leading-[0.95]">
                        Create Adventure Log
                    </h1>
                    <p className="text-slate-gray mt-3 max-w-2xl">
                        Share what you did so someone else can go do it. Add photos, a few lines, and the one thing they should know — that's it.
                    </p>
                </div>

                <div className="grid lg:grid-cols-[1fr_380px] gap-8">
                    {/* LEFT: form sections */}
                    <form onSubmit={handleSubmit} className="space-y-6 min-w-0">
                        {/* QUICK LOG — post first, add the details later */}
                        <Section icon={Camera} title="Your adventure" testid="section-quick" subtitle="Post first, add the details later — this is all you really need.">
                            <Field label="Photos & video" hint="People remember the trip through the pictures. Add them first.">
                                <MultiImageUploader
                                    testid="create-photos"
                                    value={form.photos}
                                    max={20}
                                    coverUrl={form.cover_photo}
                                    onSetCover={paid ? (url) => patch({ cover_photo: url }) : undefined}
                                    onChange={(next) => patch({
                                        photos: next,
                                        cover_photo: next.includes(form.cover_photo) ? form.cover_photo : (next[0] || ""),
                                    })}
                                />
                                {form.photos.length > 0 && (
                                    <div className="mt-3">
                                        <PhotoGallery
                                            photos={form.photos}
                                            coverPhoto={form.cover_photo}
                                            captions={form.photo_captions || {}}
                                            paid={paid}
                                            onChange={(updates) => patch(updates)}
                                        />
                                    </div>
                                )}
                            </Field>

                            <Field label="Title" required>
                                <input
                                    data-testid="create-title-input"
                                    required
                                    maxLength={120}
                                    value={form.title}
                                    onChange={(e) => patch({ title: e.target.value })}
                                    className={inputCls}
                                    placeholder="Rappelling at Red Rock Canyon"
                                />
                            </Field>

                            <Field label="Activity">
                                <ActivityPicker
                                    value={form.activity}
                                    onChange={(v) => patch({ activity: v })}
                                    testid="create-activity"
                                />
                            </Field>

                            <Field label="Location" hint="A name or general area — exact coordinates are optional (under More details).">
                                <input
                                    data-testid="create-location-input"
                                    value={form.location_name}
                                    onChange={(e) => patch({ location_name: e.target.value })}
                                    className={inputCls}
                                    placeholder="Red Rock Canyon, NV"
                                />
                            </Field>

                            <div className="grid sm:grid-cols-2 gap-4">
                                <Field label="When did you go?">
                                    <input
                                        data-testid="create-date-input"
                                        type="date"
                                        value={form.date}
                                        onChange={(e) => patch({ date: e.target.value })}
                                        className={inputCls}
                                    />
                                </Field>
                                <Field label="Difficulty">
                                    <ChipRow
                                        options={DIFFICULTIES}
                                        value={form.difficulty}
                                        onChange={(v) => patch({ difficulty: v })}
                                        testid="create-difficulty"
                                    />
                                </Field>
                            </div>

                            <Field label="Short summary" hint="One or two sentences. Shown on feed cards.">
                                <textarea
                                    data-testid="create-summary-input"
                                    rows={2}
                                    maxLength={160}
                                    value={form.summary}
                                    onChange={(e) => patch({ summary: e.target.value })}
                                    className={textareaCls}
                                    placeholder="A short afternoon rappel with great sandstone walls, easy access, and beginner-friendly routes."
                                />
                                <CharCount value={form.summary} max={160} />
                            </Field>

                            <Field label="What should someone know before going?" hint="The most useful field — parking, permits, safety, best time, conditions.">
                                <textarea
                                    data-testid="create-story_before_you_go"
                                    rows={3}
                                    value={form.story_before_you_go}
                                    onChange={(e) => patch({ story_before_you_go: e.target.value })}
                                    className={textareaCls}
                                    placeholder="Permits sell out by April. Lot fills before 8am. Bring 2L water minimum."
                                />
                            </Field>

                            <Field label="Who can see this?">
                                <ChipRow
                                    options={[
                                        { id: "public", label: "Public" },
                                        { id: "followers", label: "Followers only" },
                                        { id: "private", label: "Private" },
                                    ]}
                                    value={form.visibility}
                                    onChange={(v) => patch({ visibility: v })}
                                    testid="create-visibility"
                                />
                            </Field>
                        </Section>

                        {/* Optional detail chooser — pick only what you need */}
                        <div data-testid="more-details-chooser" className="bg-snowcap border border-ridge-line rounded-xl p-5">
                            <div className="flex items-baseline justify-between mb-1">
                                <h2 className="font-display font-black text-xl text-midnight-pine tracking-tight">Add more details</h2>
                                <span className="text-xs font-bold uppercase tracking-widest text-slate-gray">Optional</span>
                            </div>
                            <p className="text-xs text-slate-gray mb-4">
                                Turn your log into a guide. Add only the parts you want — nothing here is required.
                            </p>
                            <div className="flex flex-wrap gap-2">
                                {MORE_SECTIONS.map((s) => {
                                    const active = !!open[s.key];
                                    const Icon = s.icon;
                                    return (
                                        <button
                                            key={s.key}
                                            type="button"
                                            data-testid={`add-section-${s.key}`}
                                            onClick={() => toggleSection(s.key)}
                                            className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-full text-xs font-bold transition-all ${
                                                active
                                                    ? "bg-midnight-pine text-white"
                                                    : "bg-alpine-mist text-granite border border-ridge-line hover:border-midnight-pine"
                                            }`}
                                        >
                                            <Icon className="w-3.5 h-3.5" />
                                            {s.label}
                                            {active ? <X className="w-3 h-3 opacity-80" /> : <Plus className="w-3 h-3 opacity-60" />}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        <div className="space-y-6" data-testid="advanced-details">
                        {open.trip && (
                        <Section icon={Compass} title="Trip details" testid="section-basics">
                            <Field label="Layout style" hint="How this post should feel to a reader.">
                                <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                                    {LAYOUT_STYLES.map((l) => (
                                        <button
                                            key={l.id}
                                            type="button"
                                            data-testid={`create-layout-${l.id}`}
                                            onClick={() => patch({ layout_style: l.id })}
                                            className={`p-3 rounded-md border text-left transition-all ${
                                                form.layout_style === l.id
                                                    ? "border-ember-orange bg-ember-orange/5"
                                                    : "border-ridge-line bg-snowcap hover:border-midnight-pine"
                                            }`}
                                        >
                                            <div className="text-sm font-bold text-midnight-pine">{l.label}</div>
                                            <div className="text-[11px] text-slate-gray leading-tight mt-0.5">{l.desc}</div>
                                        </button>
                                    ))}
                                </div>
                            </Field>

                            <div className="grid sm:grid-cols-2 gap-4">
                                <Field label="End date" hint="For multi-day trips.">
                                    <input
                                        data-testid="create-date-end-input"
                                        type="date"
                                        value={form.date_end}
                                        onChange={(e) => patch({ date_end: e.target.value })}
                                        className={inputCls}
                                    />
                                </Field>
                                <Field label="Season">
                                    <ChipRow
                                        options={SEASONS}
                                        value={form.season}
                                        onChange={(v) => patch({ season: v })}
                                        testid="create-season"
                                        clearable
                                    />
                                </Field>
                            </div>

                            <div className="grid sm:grid-cols-2 gap-4">
                                <Field label="Best time to go" hint="e.g. Morning, late summer, Sept–Oct.">
                                    <input
                                        data-testid="create-best-time-input"
                                        value={form.best_time_to_go}
                                        onChange={(e) => patch({ best_time_to_go: e.target.value })}
                                        className={inputCls}
                                        placeholder="Pre-dawn, July to early September"
                                    />
                                </Field>
                                <Field label="Group type">
                                    <ChipRow
                                        options={GROUP_TYPES}
                                        value={form.group_type}
                                        onChange={(v) => patch({ group_type: v })}
                                        testid="create-group-type"
                                        clearable
                                    />
                                </Field>
                            </div>
                        </Section>
                        )}

                        {open.route && (
                        <Section icon={MapPin} title="Location & route" testid="section-location">
                            <Field label="General area" hint="A broad area instead of an exact pin (e.g. fishing/camping spots).">
                                <input
                                    data-testid="create-general-area-input"
                                    value={form.general_area}
                                    onChange={(e) => patch({ general_area: e.target.value })}
                                    className={inputCls}
                                    placeholder="Front Range, Colorado"
                                />
                            </Field>

                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                <Field label="Lat"><input data-testid="create-lat-input" type="number" step="any" value={form.lat} onChange={(e) => patch({ lat: e.target.value })} className={inputCls} placeholder="40.25" /></Field>
                                <Field label="Lng"><input data-testid="create-lng-input" type="number" step="any" value={form.lng} onChange={(e) => patch({ lng: e.target.value })} className={inputCls} placeholder="-105.61" /></Field>
                                <Field label="Distance (km)"><input data-testid="create-distance-input" type="number" step="any" value={form.distance_km} onChange={(e) => patch({ distance_km: e.target.value })} className={inputCls} placeholder="12" /></Field>
                                <Field label="Elevation gain (m)"><input data-testid="create-elevation-input" type="number" step="any" value={form.elevation_gain_m} onChange={(e) => patch({ elevation_gain_m: e.target.value })} className={inputCls} placeholder="900" /></Field>
                            </div>

                            <div className="grid sm:grid-cols-2 gap-4">
                                <Field label="Duration (hrs)"><input data-testid="create-duration-input" type="number" step="any" value={form.duration_hours} onChange={(e) => patch({ duration_hours: e.target.value })} className={inputCls} placeholder="8" /></Field>
                                <Field label="Route type">
                                    <ChipRow
                                        options={ROUTE_TYPES}
                                        value={form.route_type}
                                        onChange={(v) => patch({ route_type: v })}
                                        testid="create-route-type"
                                        clearable
                                    />
                                </Field>
                            </div>

                            <div className="grid sm:grid-cols-2 gap-4">
                                <Field label="Start point"><input data-testid="create-start-input" value={form.start_point} onChange={(e) => patch({ start_point: e.target.value })} className={inputCls} placeholder="Longs Peak Trailhead" /></Field>
                                <Field label="End point" hint="Optional, for point-to-point."><input data-testid="create-end-input" value={form.end_point} onChange={(e) => patch({ end_point: e.target.value })} className={inputCls} placeholder="" /></Field>
                            </div>

                            <Field label="Terrain">
                                <MultiChip
                                    options={TERRAIN}
                                    value={form.terrain}
                                    onChange={(v) => patch({ terrain: v })}
                                    testid="create-terrain"
                                />
                            </Field>

                            <PremiumToggleRow
                                paid={paid}
                                testid="toggle-hide-location"
                                icon={Lock}
                                title="Hide exact location from public"
                                description="Coordinates and GPS route will be fuzzed by ~5km for everyone but you. Good for sensitive fishing/shooting/camping spots."
                                checked={form.hide_exact_location}
                                onChange={(v) => patch({ hide_exact_location: v })}
                            />

                            <GpxUploadRow form={form} patch={patch} />

                            {form.lat && form.lng && !isNaN(parseFloat(form.lat)) && !isNaN(parseFloat(form.lng)) && (
                                <div className="h-[200px] rounded-md overflow-hidden border border-ridge-line">
                                    <MapView
                                        center={[parseFloat(form.lat), parseFloat(form.lng)]}
                                        zoom={11}
                                        markers={[{ position: [parseFloat(form.lat), parseFloat(form.lng)] }]}
                                    />
                                </div>
                            )}
                        </Section>
                        )}

                        {open.conditions && (
                        <Section icon={Mountain} title="Conditions & access" testid="section-difficulty">
                            <div className="grid sm:grid-cols-2 gap-4">
                                <Field label="Physical demand"><ChipRow options={PHYSICAL} value={form.physical_demand} onChange={(v) => patch({ physical_demand: v })} testid="create-physical" clearable /></Field>
                                <Field label="Technical skill"><ChipRow options={TECHNICAL} value={form.technical_skill} onChange={(v) => patch({ technical_skill: v })} testid="create-technical" clearable /></Field>
                            </div>

                            <div className="grid sm:grid-cols-2 gap-4">
                                <Field label="Trail / route condition"><ChipRow options={TRAIL_CONDITIONS} value={form.trail_condition} onChange={(v) => patch({ trail_condition: v })} testid="create-trail-condition" clearable /></Field>
                                <Field label="Weather"><ChipRow options={WEATHER_OPTS} value={form.weather} onChange={(v) => patch({ weather: v })} testid="create-weather" clearable /></Field>
                            </div>

                            <div className="grid sm:grid-cols-2 gap-4">
                                <Field label="Water crossings"><ChipRow options={WATER_CROSSINGS} value={form.water_crossings} onChange={(v) => patch({ water_crossings: v })} testid="create-water" clearable /></Field>
                                <Field label="Cell service"><ChipRow options={CELL_SERVICE} value={form.cell_service} onChange={(v) => patch({ cell_service: v })} testid="create-cell" clearable /></Field>
                            </div>

                            <Field label="Crowds">
                                <ChipRow options={CROWDS} value={form.crowds} onChange={(v) => patch({ crowds: v })} testid="create-crowds" clearable />
                            </Field>

                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                <YesNoChip label="Permit required" value={form.permit_required} onChange={(v) => patch({ permit_required: v })} testid="permit" />
                                <YesNoChip label="Fees required" value={form.fees_required} onChange={(v) => patch({ fees_required: v })} testid="fees" />
                                <YesNoChip label="Kid friendly" value={form.kid_friendly} onChange={(v) => patch({ kid_friendly: v })} testid="kid" icon={Baby} />
                                <YesNoChip label="Dog friendly" value={form.dog_friendly} onChange={(v) => patch({ dog_friendly: v })} testid="dog" icon={Dog} />
                            </div>
                        </Section>
                        )}

                        {open.media && (
                        <>
                        <Section icon={Film} title="Video clips" testid="section-videos" subtitle="Up to 3 short clips (mp4 / webm / mov, 50MB each).">
                            <MultiVideoUploader
                                testid="create-videos"
                                value={form.videos}
                                max={3}
                                onChange={(next) => patch({ videos: next })}
                            />
                        </Section>

                        <Section icon={ImageIcon} title="Before / After" testid="section-before-after" subtitle="Show how a place changed — restoration, weather, seasons, conditions.">
                            <BeforeAfterUploader
                                testid="create-before-after"
                                value={form.before_after}
                                max={5}
                                onChange={(next) => patch({ before_after: next })}
                            />
                        </Section>

                        <Section icon={MapIcon} title="Map screenshots" testid="section-map-shots" subtitle="Drop annotated map images so others can plan their route.">
                            <MultiImageUploader
                                testid="create-map-shots"
                                value={form.map_screenshots}
                                max={6}
                                onChange={(next) => patch({ map_screenshots: next })}
                            />
                        </Section>
                        </>
                        )}

                        {open.story && (
                        <Section icon={BookOpen} title="Tell the story" testid="section-story">
                            <Field label="Description (free-form)" hint="Optional — the story prompts below will also be stitched into your post.">
                                <textarea
                                    data-testid="create-description-input"
                                    rows={5}
                                    value={form.description}
                                    onChange={(e) => patch({ description: e.target.value })}
                                    className={textareaCls}
                                    placeholder="The headlamp glow at 3am, the first warm blink on the ridgeline…"
                                />
                            </Field>

                            <div className="space-y-3">
                                {STORY_PROMPTS.filter((p) => p.key !== "story_before_you_go").map((p) => (
                                    <Field key={p.key} label={p.label}>
                                        <textarea
                                            data-testid={`create-${p.key}`}
                                            rows={2}
                                            value={form[p.key]}
                                            onChange={(e) => patch({ [p.key]: e.target.value })}
                                            className={textareaCls}
                                            placeholder={p.placeholder}
                                        />
                                    </Field>
                                ))}
                            </div>
                        </Section>
                        )}

                        {open.gear && (
                        <Section icon={Backpack} title="Gear & prep" testid="section-gear">
                            <Field label="Gear used"><TagInput value={form.gear_used} onChange={(v) => patch({ gear_used: v })} placeholder="Add gear, press Enter" testid="gear-used" /></Field>
                            <Field label="Link gear from Marketplace" hint="Attach actual listings so others can buy or borrow what you used.">
                                <MarketplaceGearPicker
                                    value={form.linked_gear}
                                    onChange={(v) => patch({ linked_gear: v })}
                                />
                            </Field>
                            <Field label="Must-have items" hint="Things others should not forget."><TagInput value={form.must_haves} onChange={(v) => patch({ must_haves: v })} placeholder="Headlamp, microspikes, water filter" testid="must-haves" /></Field>
                            <Field label="Nice-to-haves"><TagInput value={form.nice_to_haves} onChange={(v) => patch({ nice_to_haves: v })} placeholder="Trekking poles, camp chair" testid="nice-to-haves" /></Field>
                            <Field label="Safety gear"><TagInput value={form.safety_gear} onChange={(v) => patch({ safety_gear: v })} placeholder="First aid, GPS beacon, bear spray" testid="safety-gear" /></Field>
                            <Field label="What I wish I brought">
                                <textarea
                                    data-testid="create-wished-input"
                                    rows={2}
                                    value={form.wished_i_brought}
                                    onChange={(e) => patch({ wished_i_brought: e.target.value })}
                                    className={textareaCls}
                                    placeholder="A second pair of socks. Lesson learned."
                                />
                            </Field>
                            <Field label="Vehicle needed">
                                <ChipRow options={VEHICLES} value={form.vehicle_needed} onChange={(v) => patch({ vehicle_needed: v })} testid="create-vehicle" clearable />
                            </Field>
                        </Section>
                        )}

                        {open.ratings && (
                        <Section icon={Heart} title="Recommendations & ratings" testid="section-recommend">
                            <Field label="Best for">
                                <MultiChip options={BEST_FOR_OPTS} value={form.best_for} onChange={(v) => patch({ best_for: v })} testid="best-for" />
                            </Field>
                            <Field label="Would you recommend?">
                                <ChipRow
                                    options={RECOMMEND}
                                    value={form.would_recommend}
                                    onChange={(v) => patch({ would_recommend: v })}
                                    testid="recommend"
                                    clearable
                                />
                            </Field>
                            <Field label="Verdict badge" hint="Shown on your post like a stamp.">
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                                    {WORTH_IT.map((w) => (
                                        <button
                                            key={w.id}
                                            type="button"
                                            data-testid={`worthit-${w.id}`}
                                            onClick={() => patch({ worth_it_badge: form.worth_it_badge === w.id ? "" : w.id })}
                                            className={`p-3 rounded-md border transition-all text-left ${
                                                form.worth_it_badge === w.id
                                                    ? "border-ember-orange bg-ember-orange/5"
                                                    : "border-ridge-line bg-snowcap hover:border-midnight-pine"
                                            }`}
                                        >
                                            <div className="text-2xl mb-1">{w.emoji}</div>
                                            <div className="text-sm font-bold text-midnight-pine">{w.label}</div>
                                        </button>
                                    ))}
                                </div>
                            </Field>
                            <Field label="Tips for others">
                                <textarea
                                    data-testid="create-tips-input"
                                    rows={3}
                                    value={form.tips_for_others}
                                    onChange={(e) => patch({ tips_for_others: e.target.value })}
                                    className={textareaCls}
                                    placeholder="Start before sunrise. Bring layers. Don't trust the cairns past Boulder Field."
                                />
                            </Field>
                            <Field label="Ratings" hint="Five stars = excellent.">
                                <div className="space-y-2">
                                    {RATING_KEYS.map((r) => (
                                        <div key={r.key} className="flex items-center justify-between gap-3">
                                            <span className="text-sm text-granite">{r.label}</span>
                                            <StarRow
                                                value={form.ratings?.[r.key] || 0}
                                                onChange={(v) => patch({ ratings: { ...form.ratings, [r.key]: v } })}
                                                testid={`rating-${r.key}`}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </Field>
                        </Section>
                        )}

                        {open.safety && (
                        <Section icon={ShieldCheck} title="Safety & ethics" testid="section-safety">
                            <ToggleRow
                                testid="leave-no-trace"
                                icon={Sparkles}
                                title="Leave No Trace reminder"
                                description="Add a Leave-No-Trace stamp to your post — encourages responsible recreation."
                                checked={form.leave_no_trace}
                                onChange={(v) => patch({ leave_no_trace: v })}
                            />
                            <PremiumToggleRow
                                paid={paid}
                                testid="sensitive-location"
                                icon={Lock}
                                title="Sensitive location warning"
                                description="Flags this as a fragile spot. Shows a warning banner and fuzzes coordinates for everyone but you. Great for shooting / fishing honey holes."
                                checked={form.sensitive_location}
                                onChange={(v) => patch({ sensitive_location: v })}
                            />
                            <ToggleRow
                                testid="private-land"
                                icon={ShieldCheck}
                                title="Private land warning"
                                description="Reminds readers to check land status before going."
                                checked={form.private_land_warning}
                                onChange={(v) => patch({ private_land_warning: v })}
                            />
                            <Field label="Wildlife encounter notes">
                                <input data-testid="wildlife-input" value={form.wildlife_notes} onChange={(e) => patch({ wildlife_notes: e.target.value })} className={inputCls} placeholder="Saw a mama bear with two cubs at mile 4" />
                            </Field>
                            <Field label="Fire restrictions">
                                <input data-testid="fire-input" value={form.fire_restrictions} onChange={(e) => patch({ fire_restrictions: e.target.value })} className={inputCls} placeholder="Stage 2 — no campfires" />
                            </Field>
                            <Field label="Trail closure / hazard">
                                <input data-testid="closure-input" value={form.trail_closure_warning} onChange={(e) => patch({ trail_closure_warning: e.target.value })} className={inputCls} placeholder="Bridge out at mile 6 — reroute via south ridge" />
                            </Field>
                            <Field label="Required licenses / permits">
                                <input data-testid="licenses-input" value={form.required_licenses} onChange={(e) => patch({ required_licenses: e.target.value })} className={inputCls} placeholder="Rocky Mountain timed-entry pass, GA fishing license" />
                            </Field>
                        </Section>
                        )}

                        {open.tags && (
                        <Section icon={Eye} title="Tags & privacy" testid="section-privacy">
                            <Field label="Tags" hint="Comma-separated. Used for search & recommendations.">
                                <input data-testid="create-tags-input" value={form.tags} onChange={(e) => patch({ tags: e.target.value })} className={inputCls} placeholder="14er, summit, alpine, sunrise" />
                            </Field>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <ToggleRow
                                    testid="allow-comments"
                                    icon={Users}
                                    title="Allow comments"
                                    description=""
                                    checked={form.allow_comments}
                                    onChange={(v) => patch({ allow_comments: v })}
                                    compact
                                />
                                <ToggleRow
                                    testid="allow-saves"
                                    icon={Heart}
                                    title="Allow saves"
                                    description=""
                                    checked={form.allow_saves}
                                    onChange={(v) => patch({ allow_saves: v })}
                                    compact
                                />
                            </div>
                        </Section>
                        )}
                        </div>

                        {/* Action bar */}
                        <div
                            data-testid="create-action-bar"
                            className="sticky bottom-16 lg:bottom-4 z-10 mt-6 bg-snowcap border border-ridge-line rounded-lg p-3 shadow-lg flex flex-wrap items-center gap-2"
                        >
                            <button
                                type="button"
                                data-testid="create-cancel-btn"
                                onClick={() => navigate(-1)}
                                className="px-4 py-2.5 rounded-md border border-ridge-line text-granite font-bold text-sm hover:bg-alpine-mist transition-all"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                data-testid="create-savedraft-btn"
                                disabled={paid && savingDraft}
                                onClick={() => paid ? saveDraft() : navigate("/pricing")}
                                title={paid ? "Save draft" : "Upgrade to save drafts"}
                                className={`px-4 py-2.5 rounded-md font-bold text-sm inline-flex items-center gap-2 transition-all ${
                                    isEditMode ? "hidden" : ""
                                } ${
                                    paid
                                        ? "border border-ridge-line text-granite hover:bg-alpine-mist"
                                        : "border border-ember-orange/40 text-ember-orange bg-ember-orange/5 hover:bg-ember-orange/10"
                                }`}
                            >
                                {paid ? <Save className="w-4 h-4" /> : <Crown className="w-4 h-4" />}
                                {paid ? (savingDraft ? "Saving…" : "Save draft") : "Save draft (Adventure +)"}
                            </button>
                            <div className="flex-1" />
                            {lastSavedAt && paid && !isEditMode && (
                                <span className="text-xs text-slate-gray hidden sm:inline">
                                    Auto-saved
                                </span>
                            )}
                            <button
                                type="submit"
                                data-testid="create-submit-btn"
                                disabled={loading || !form.title.trim()}
                                className="px-6 py-2.5 rounded-md bg-ember-orange text-white font-bold text-sm hover:bg-ember-dark active:scale-[0.99] disabled:opacity-60 transition-all inline-flex items-center gap-2"
                            >
                                <Send className="w-4 h-4" />
                                {loading
                                    ? (isEditMode ? "Saving…" : "Publishing…")
                                    : (isEditMode ? "Save changes" : "Publish Adventure Log")}
                            </button>
                        </div>
                    </form>

                    {/* RIGHT: sticky preview + checklist */}
                    <aside className="hidden lg:block">
                        <div className="sticky top-6 space-y-4">
                            <ChecklistPanel completion={completion} />
                            <PreviewPanel adventure={previewAdventure} />
                        </div>
                    </aside>
                </div>
            </div>
        </div>
    );
}

// ---------- helpers ----------
const inputCls = "w-full px-4 py-3 rounded-md bg-snowcap border border-ridge-line focus:border-ember-orange focus:outline-none focus:ring-2 focus:ring-ember-orange/20 transition-colors text-granite";
const textareaCls = inputCls + " resize-none";

/**
 * Reverse-parse a stitched `description` (built by `buildDescription`) into
 * its individual story-prompt fields. Used when editing **legacy** posts
 * created before the `story_*` columns were persisted on the model.
 *
 * Input shape (post `\n\n` separator):
 *   Freeform text...
 *
 *   **What made this adventure worth sharing?**
 *   The ridgeline at sunrise...
 *
 *   **Tips for others**
 *   Bring extra water...
 *
 * Returns `{ description, story_worth_sharing, ..., tips_for_others, wished_i_brought }`
 * containing ONLY the keys it could extract — caller spreads over the form.
 */
function unstitchDescription(stitched) {
    if (!stitched || typeof stitched !== "string") return {};
    // Label → form-key map (mirrors buildDescription).
    const labelMap = {};
    STORY_PROMPTS.forEach((p) => { labelMap[p.label.toLowerCase()] = p.key; });
    labelMap["tips for others"] = "tips_for_others";
    labelMap["what i wish i brought"] = "wished_i_brought";

    const out = {};
    const blocks = stitched.split(/\n\n+/);
    const freeform = [];
    for (const block of blocks) {
        const m = block.match(/^\*\*(.+?)\*\*\n([\s\S]*)$/);
        if (m) {
            const key = labelMap[m[1].trim().toLowerCase()];
            if (key) {
                out[key] = m[2].trim();
                continue;
            }
        }
        freeform.push(block);
    }
    out.description = freeform.join("\n\n").trim();
    return out;
}

function buildDescription(form) {
    const parts = [];
    if (form.description?.trim()) parts.push(form.description.trim());
    const promptOrder = STORY_PROMPTS;
    promptOrder.forEach((p) => {
        const v = form[p.key];
        if (v?.trim()) parts.push(`**${p.label}**\n${v.trim()}`);
    });
    if (form.tips_for_others?.trim()) parts.push(`**Tips for others**\n${form.tips_for_others.trim()}`);
    if (form.wished_i_brought?.trim()) parts.push(`**What I wish I brought**\n${form.wished_i_brought.trim()}`);
    return parts.join("\n\n");
}

function computeCompletion(form) {
    const checks = [
        { key: "title", label: "Title added", done: !!form.title.trim() },
        { key: "summary", label: "Short summary", done: !!form.summary.trim() },
        { key: "location", label: "Location set", done: !!form.location_name.trim() },
        { key: "photos", label: "Photos added", done: form.photos.length > 0 },
        { key: "cover", label: "Cover photo selected", done: !!form.cover_photo, soft: true },
        { key: "story", label: "Story / description", done: !!form.description.trim() || STORY_PROMPTS.some((p) => form[p.key]?.trim()) },
        { key: "gear", label: "Gear notes", done: form.gear_used.length > 0 || form.must_haves.length > 0, soft: true },
        { key: "conditions", label: "Conditions noted", done: !!(form.weather || form.cell_service || form.crowds), soft: true },
        { key: "recommend", label: "Recommendation set", done: !!form.would_recommend, soft: true },
        { key: "best_for", label: "Best-for tagged", done: form.best_for.length > 0, soft: true },
    ];
    const total = checks.length;
    const done = checks.filter((c) => c.done).length;
    return { checks, percent: Math.round((done / total) * 100) };
}

function buildPreview(form, user) {
    const photos = form.photos.length
        ? [form.cover_photo || form.photos[0], ...form.photos.filter((p) => p !== (form.cover_photo || form.photos[0]))]
        : [];
    return {
        id: "preview",
        title: form.title || "Your adventure title",
        photos,
        activity: form.activity || "hiking",
        location_name: form.location_name || form.general_area || "",
        distance_km: form.distance_km ? parseFloat(form.distance_km) : null,
        difficulty: form.difficulty,
        author: {
            user_id: user?.user_id || "preview",
            name: user?.name || "You",
            avatar: user?.avatar || "",
        },
        likes_count: 0,
        comments_count: 0,
        saves_count: 0,
        shares_count: 0,
        liked: false,
        saved: false,
        created_at: new Date().toISOString(),
    };
}

// ---------- subcomponents ----------
function Section({ icon: Icon, title, testid, subtitle, children }) {
    return (
        <section
            data-testid={testid}
            className="bg-snowcap border border-ridge-line rounded-xl p-5 sm:p-6 space-y-5"
        >
            <header className="flex items-center gap-3 pb-3 border-b border-ridge-line">
                <div className="w-9 h-9 rounded-md bg-trail-tan text-midnight-pine flex items-center justify-center shrink-0">
                    <Icon className="w-5 h-5" />
                </div>
                <div>
                    <h2 className="font-display font-black text-xl text-midnight-pine tracking-tight leading-tight">
                        {title}
                    </h2>
                    {subtitle && <p className="text-xs text-slate-gray mt-0.5">{subtitle}</p>}
                </div>
            </header>
            {children}
        </section>
    );
}

function Field({ label, required, hint, children }) {
    return (
        <div>
            <label className="text-xs font-bold uppercase tracking-widest text-slate-gray mb-1.5 block">
                {label} {required && <span className="text-ember-orange">*</span>}
            </label>
            {hint && <p className="text-xs text-slate-gray mb-2">{hint}</p>}
            {children}
        </div>
    );
}

function CharCount({ value, max }) {
    const len = (value || "").length;
    const danger = len > max * 0.9;
    return (
        <div className={`text-[11px] mt-1 text-right ${danger ? "text-trail-red" : "text-slate-gray"}`}>
            {len}/{max}
        </div>
    );
}

function ChipRow({ options, value, onChange, testid, clearable = false }) {
    return (
        <div className="flex flex-wrap gap-2">
            {options.map((opt) => {
                const id = typeof opt === "string" ? opt : opt.id;
                const label = typeof opt === "string" ? opt : opt.label;
                const active = value === id;
                return (
                    <button
                        key={id}
                        type="button"
                        data-testid={`${testid}-${id}`}
                        onClick={() => onChange(active && clearable ? "" : id)}
                        className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider transition-all ${
                            active
                                ? "bg-ember-orange text-white"
                                : "bg-snowcap text-granite border border-ridge-line hover:border-ember-orange"
                        }`}
                    >
                        {label}
                    </button>
                );
            })}
        </div>
    );
}

function MultiChip({ options, value, onChange, testid }) {
    const toggle = (opt) => {
        if (value.includes(opt)) onChange(value.filter((v) => v !== opt));
        else onChange([...value, opt]);
    };
    return (
        <div className="flex flex-wrap gap-2">
            {options.map((opt) => {
                const active = value.includes(opt);
                return (
                    <button
                        key={opt}
                        type="button"
                        data-testid={`${testid}-${opt}`}
                        onClick={() => toggle(opt)}
                        className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider transition-all capitalize ${
                            active
                                ? "bg-midnight-pine text-white"
                                : "bg-snowcap text-granite border border-ridge-line hover:border-midnight-pine"
                        }`}
                    >
                        {opt}
                    </button>
                );
            })}
        </div>
    );
}

function YesNoChip({ label, value, onChange, testid, icon: Icon }) {
    return (
        <button
            type="button"
            data-testid={`yesno-${testid}`}
            onClick={() => onChange(!value)}
            className={`p-2.5 rounded-md border text-center transition-all ${
                value
                    ? "border-ember-orange bg-ember-orange/5 text-midnight-pine"
                    : "border-ridge-line bg-snowcap text-slate-gray hover:border-midnight-pine"
            }`}
        >
            {Icon && <Icon className={`w-4 h-4 mx-auto mb-1 ${value ? "text-ember-orange" : ""}`} />}
            <div className="text-xs font-bold">{label}</div>
            <div className="text-[10px] uppercase tracking-widest mt-0.5">{value ? "Yes" : "No"}</div>
        </button>
    );
}

function TagInput({ value, onChange, placeholder, testid }) {
    const [draft, setDraft] = useState("");
    const add = () => {
        const t = draft.trim();
        if (!t) return;
        if (!value.includes(t)) onChange([...value, t]);
        setDraft("");
    };
    return (
        <div className="space-y-2">
            <div className="flex gap-2">
                <input
                    data-testid={`taginput-${testid}`}
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === ",") {
                            e.preventDefault();
                            add();
                        }
                    }}
                    placeholder={placeholder}
                    className={inputCls + " flex-1"}
                />
                <button
                    type="button"
                    data-testid={`taginput-add-${testid}`}
                    onClick={add}
                    className="px-4 rounded-md bg-midnight-pine text-white font-bold text-sm hover:bg-deep-evergreen transition-all"
                >
                    <Plus className="w-4 h-4" />
                </button>
            </div>
            {value.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                    {value.map((t) => (
                        <span
                            key={t}
                            className="inline-flex items-center gap-1.5 pl-3 pr-1.5 py-1 rounded-full bg-trail-tan/60 text-midnight-pine text-xs font-bold"
                        >
                            {t}
                            <button
                                type="button"
                                data-testid={`taginput-remove-${testid}-${t}`}
                                onClick={() => onChange(value.filter((x) => x !== t))}
                                className="w-4 h-4 rounded-full hover:bg-midnight-pine/10 flex items-center justify-center"
                            >
                                <X className="w-3 h-3" />
                            </button>
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
}

function StarRow({ value, onChange, testid }) {
    return (
        <div className="flex items-center gap-0.5" data-testid={testid}>
            {[1, 2, 3, 4, 5].map((n) => (
                <button
                    key={n}
                    type="button"
                    data-testid={`${testid}-${n}`}
                    onClick={() => onChange(value === n ? 0 : n)}
                    className="p-1 group"
                    aria-label={`${n} of 5 stars`}
                >
                    <Star
                        className={`w-4 h-4 transition-all ${
                            n <= value
                                ? "fill-ember-orange text-ember-orange"
                                : "text-slate-gray group-hover:text-ember-orange"
                        }`}
                    />
                </button>
            ))}
        </div>
    );
}

function ToggleRow({ icon: Icon, title, description, checked, onChange, testid, compact }) {
    return (
        <div
            data-testid={`togglerow-${testid}`}
            className={`flex items-start gap-3 ${compact ? "p-3" : "p-4"} rounded-md border border-ridge-line bg-alpine-mist/40`}
        >
            {Icon && (
                <div className="w-9 h-9 rounded-md bg-trail-tan text-midnight-pine flex items-center justify-center shrink-0">
                    <Icon className="w-4 h-4" />
                </div>
            )}
            <div className="flex-1 min-w-0">
                <div className="font-bold text-granite text-sm">{title}</div>
                {description && <p className="text-xs text-slate-gray mt-0.5">{description}</p>}
            </div>
            <button
                type="button"
                role="switch"
                aria-checked={checked}
                data-testid={`toggle-${testid}`}
                onClick={() => onChange(!checked)}
                className={`relative w-11 h-6 shrink-0 rounded-full transition-colors mt-0.5 ${
                    checked ? "bg-ember-orange" : "bg-ridge-line"
                }`}
            >
                <span
                    className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-snowcap shadow-sm transition-transform ${
                        checked ? "translate-x-5" : "translate-x-0"
                    }`}
                />
            </button>
        </div>
    );
}

function PremiumToggleRow({ paid, icon: Icon, title, description, checked, onChange, testid }) {
    if (paid) {
        return (
            <ToggleRow
                icon={Icon}
                title={title}
                description={description}
                checked={checked}
                onChange={onChange}
                testid={testid}
            />
        );
    }
    return (
        <div
            data-testid={`premium-locked-${testid}`}
            className="flex items-start gap-3 p-4 rounded-md border border-ember-orange/30 bg-ember-orange/5"
        >
            <div className="w-9 h-9 rounded-md bg-ember-orange text-white flex items-center justify-center shrink-0">
                <Crown className="w-4 h-4" />
            </div>
            <div className="flex-1 min-w-0">
                <div className="font-bold text-granite text-sm flex items-center gap-2">
                    {title}
                    <span className="px-1.5 py-0.5 rounded-full bg-ember-orange text-white text-[9px] font-bold uppercase tracking-widest">
                        Adventure +
                    </span>
                </div>
                <p className="text-xs text-slate-gray mt-0.5">{description}</p>
            </div>
            <a
                href="/pricing"
                data-testid={`premium-upsell-${testid}`}
                className="text-xs font-bold text-ember-orange hover:underline whitespace-nowrap"
            >
                Upgrade →
            </a>
        </div>
    );
}

function PhotoGallery({ photos, coverPhoto, captions, paid, onChange }) {
    const dragFrom = useRef(null);

    const onDragStart = (i) => () => { dragFrom.current = i; };
    const onDragOver = (e) => e.preventDefault();
    const onDrop = (i) => () => {
        const from = dragFrom.current;
        if (from === null || from === i) return;
        const next = [...photos];
        const [moved] = next.splice(from, 1);
        next.splice(i, 0, moved);
        dragFrom.current = null;
        onChange({ photos: next });
    };

    return (
        <div className="space-y-3 mt-4 pt-4 border-t border-ridge-line">
            <div className="flex items-center justify-between">
                <h3 className="text-sm font-bold text-granite">Your gallery ({photos.length})</h3>
                <span className="text-[10px] uppercase tracking-widest text-slate-gray">
                    Drag to reorder
                </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {photos.map((p, i) => {
                    const isCover = (coverPhoto || photos[0]) === p;
                    return (
                        <div
                            key={p}
                            draggable
                            onDragStart={onDragStart(i)}
                            onDragOver={onDragOver}
                            onDrop={onDrop(i)}
                            data-testid={`gallery-item-${i}`}
                            className={`relative group rounded-md overflow-hidden border-2 transition-all ${
                                isCover ? "border-ember-orange shadow-md" : "border-ridge-line hover:border-midnight-pine"
                            }`}
                        >
                            <img src={resolveImageUrl(p)} alt="" className="w-full aspect-[16/10] object-cover" />
                            <div className="absolute top-2 left-2 flex items-center gap-1">
                                <span className="px-2 py-0.5 rounded bg-snowcap/95 backdrop-blur text-[10px] font-bold">
                                    {i + 1}
                                </span>
                                {isCover && (
                                    <span className="px-2 py-0.5 rounded bg-ember-orange text-white text-[10px] font-bold uppercase tracking-widest">
                                        Cover
                                    </span>
                                )}
                            </div>
                            <div className="absolute top-2 right-2 flex items-center gap-1">
                                <span className="w-7 h-7 rounded-md bg-snowcap/95 backdrop-blur flex items-center justify-center text-slate-gray cursor-grab">
                                    <GripVertical className="w-4 h-4" />
                                </span>
                            </div>
                            <div className="p-2 bg-snowcap">
                                <input
                                    data-testid={`gallery-caption-${i}`}
                                    value={captions[p] || ""}
                                    onChange={(e) =>
                                        onChange({ photo_captions: { ...captions, [p]: e.target.value } })
                                    }
                                    placeholder="Add a caption…"
                                    className="w-full text-xs text-granite px-2 py-1.5 rounded bg-alpine-mist focus:outline-none focus:ring-2 focus:ring-ember-orange/20"
                                />
                                <div className="flex items-center gap-2 mt-2">
                                    {paid ? (
                                        <button
                                            type="button"
                                            data-testid={`gallery-set-cover-${i}`}
                                            onClick={() => onChange({ cover_photo: p })}
                                            className="text-[10px] font-bold uppercase tracking-widest text-ember-orange hover:underline"
                                        >
                                            {isCover ? "✓ Cover photo" : "Set as cover"}
                                        </button>
                                    ) : (
                                        <a
                                            href="/pricing"
                                            data-testid={`gallery-cover-locked-${i}`}
                                            className="text-[10px] font-bold uppercase tracking-widest text-ember-orange hover:underline inline-flex items-center gap-1"
                                            title="Cover photo selector is an Adventure / Premium feature"
                                        >
                                            <Crown className="w-3 h-3" /> Cover (Adventure +)
                                        </a>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function ChecklistPanel({ completion }) {
    const tips = completion.checks.filter((c) => !c.done).slice(0, 4);
    return (
        <div
            data-testid="completion-panel"
            className="bg-snowcap border border-ridge-line rounded-xl p-5"
        >
            <h3 className="font-display font-black text-base text-midnight-pine mb-1">
                Make this more helpful
            </h3>
            <p className="text-xs text-slate-gray mb-3">
                Totally optional — even a photo, a location, and one good tip makes a great log.
            </p>
            {tips.length > 0 ? (
                <ul className="space-y-1.5">
                    {tips.map((c) => (
                        <li
                            key={c.key}
                            data-testid={`checklist-${c.key}`}
                            className="flex items-center gap-2 text-xs text-slate-gray"
                        >
                            <span className="w-1.5 h-1.5 rounded-full bg-ember-orange/60 shrink-0" />
                            {c.label}
                        </li>
                    ))}
                </ul>
            ) : (
                <div className="text-xs font-semibold text-moss flex items-center gap-2">
                    <Sparkles className="w-3.5 h-3.5" /> Looking great — ready to publish.
                </div>
            )}
        </div>
    );
}

function PreviewPanel({ adventure }) {
    return (
        <div data-testid="preview-panel">
            <div className="text-xs font-bold uppercase tracking-[0.25em] text-slate-gray mb-2 flex items-center gap-2">
                <ImageIcon className="w-3 h-3" /> Live preview
            </div>
            <AdventureCard adventure={adventure} />
        </div>
    );
}

function GpxUploadRow({ form, patch }) {
    const inputRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const hasRoute = (form.route?.length || 0) > 0;

    const handleFile = async (file) => {
        if (!file) return;
        const lower = file.name.toLowerCase();
        if (!lower.endsWith(".gpx") && !lower.endsWith(".kml")) {
            toast.error("Only .gpx or .kml files are accepted.");
            return;
        }
        const fd = new FormData();
        fd.append("file", file);
        setUploading(true);
        try {
            const { data } = await api.post("/uploads/gpx", fd, {
                headers: { "Content-Type": undefined },
            });
            const updates = {
                route: data.route || [],
                gpx_url: data.url || "",
            };
            // Fill distance if empty, and start/end coords if empty
            if (!form.distance_km && data.distance_km) updates.distance_km = String(data.distance_km);
            if ((!form.lat || !form.lng) && data.start) {
                updates.lat = String(data.start[0]);
                updates.lng = String(data.start[1]);
            }
            patch(updates);
            toast.success(`GPX loaded — ${data.point_count} trackpoints`);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setUploading(false);
        }
    };

    return (
        <div
            data-testid="gpx-upload-row"
            className="flex items-center gap-3 p-3 rounded-md bg-alpine-mist border border-ridge-line"
        >
            <div className="w-9 h-9 rounded-md bg-snowcap flex items-center justify-center shrink-0">
                <MapPin className="w-4 h-4 text-ember-orange" />
            </div>
            <div className="flex-1 min-w-0">
                <div className="text-sm font-bold text-midnight-pine">
                    {hasRoute ? `Route loaded — ${form.route.length} points` : "Upload a GPX or KML route"}
                </div>
                <div className="text-xs text-slate-gray">
                    {hasRoute
                        ? "Start coords + distance were auto-filled."
                        : "We parse track points, distance, and fill start coords. Both .gpx and .kml work."}
                </div>
            </div>
            <input
                ref={inputRef}
                type="file"
                accept=".gpx,.kml,application/gpx+xml,application/vnd.google-earth.kml+xml"
                hidden
                data-testid="create-gpx-input"
                onChange={(e) => {
                    const f = e.target.files?.[0];
                    e.target.value = "";
                    if (f) handleFile(f);
                }}
            />
            {hasRoute && (
                <button
                    type="button"
                    data-testid="create-gpx-remove"
                    onClick={() => patch({ route: [], gpx_url: "" })}
                    className="px-3 py-1.5 rounded-md text-xs font-bold border border-ridge-line text-granite hover:bg-snowcap"
                >
                    Remove
                </button>
            )}
            <button
                type="button"
                data-testid="create-gpx-upload"
                onClick={() => inputRef.current?.click()}
                disabled={uploading}
                className="px-3 py-1.5 rounded-md text-xs font-bold bg-midnight-pine text-white hover:bg-midnight-pine/90 disabled:opacity-60"
            >
                {uploading ? "Parsing…" : hasRoute ? "Replace" : "Upload GPX/KML"}
            </button>
        </div>
    );
}

